import { useState, useEffect, useRef } from 'react'
import { Home, Users, Bookmark, Info, Bug, Calendar, Image } from 'lucide-react'
import { UserMenu } from '../forms/UserMenu'
import { useLocation } from 'react-router-dom'
import { BookmarksModal } from '../bookmarks/BookmarksModal'
import { AboutModal } from '../modals/AboutModal'
import { ReportBugModal } from '../modals/ReportBugModal'
import { useAppStore } from '@cya-routes/shared'

interface HeaderProps {
  onLoadRoutes?: () => void
}

export function Header({ onLoadRoutes }: HeaderProps = {}) {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isCommunitiesOpen, setIsCommunitiesOpen] = useState(false)
  const [isBookmarksModalOpen, setIsBookmarksModalOpen] = useState(false)
  const [isAboutModalOpen, setIsAboutModalOpen] = useState(false)
  const [isReportBugModalOpen, setIsReportBugModalOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const isHomePage = location.pathname === '/'
  const { auth } = useAppStore()
  const isAuthenticated = auth.isAuthenticated

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCommunitiesOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  return (
    <>
      {/* Floating icon toolbar - positioned top right */}
      <div className="fixed top-4 right-4 z-50 hidden md:flex items-center space-x-3">
            {/* Home icon - only show when not on homepage */}
            {!isHomePage && (
              <a
                href="/"
                className="relative flex items-center justify-center w-8 h-8 rounded-full hover:opacity-80 transition-opacity duration-200"
                title="Home"
              >
                <div className="w-8 h-8 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center group hover:bg-black/70 transition-all border border-white/10 shadow-lg">
                  <Home className="w-4 h-4 text-gray-200 group-hover:text-white" />
                </div>
              </a>
            )}
            
            {/* Calendar icon */}
            <a
              href="/calendar"
              className="relative flex items-center justify-center w-8 h-8 rounded-full hover:opacity-80 transition-opacity duration-200"
              title="Events Calendar"
            >
              <div className="w-8 h-8 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center group hover:bg-black/70 transition-all border border-white/10 shadow-lg">
                <Calendar className="w-4 h-4 text-gray-200 group-hover:text-white" />
              </div>
            </a>
            
            {/* Communities Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsCommunitiesOpen(!isCommunitiesOpen)}
                className="relative flex items-center justify-center w-8 h-8 rounded-full hover:opacity-80 transition-opacity duration-200"
                title="Communities"
              >
                <div className="w-8 h-8 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center group hover:bg-black/70 transition-all border border-white/10 shadow-lg">
                  <Users className="w-4 h-4 text-gray-200 group-hover:text-white" />
                </div>
              </button>

              {/* Communities Dropdown Menu */}
              {isCommunitiesOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-gradient-to-b from-black/60 to-gray-950/70 backdrop-blur-2xl rounded-xl shadow-2xl shadow-black/50 border border-white/[0.06] z-10 ring-1 ring-inset ring-white/[0.05]">
                  <div className="py-1">
                    <a
                      href="/create-club"
                      className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/[0.05] hover:text-emerald-400 transition-colors duration-200"
                      onClick={() => setIsCommunitiesOpen(false)}
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Create a Club
                    </a>
                    <div className="border-t border-white/[0.06]"></div>
                    <a
                      href="/clubs"
                      className="flex items-center px-4 py-2 text-sm text-gray-300 hover:bg-white/[0.05] hover:text-emerald-400 transition-colors duration-200"
                      onClick={() => setIsCommunitiesOpen(false)}
                    >
                      <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Browse Clubs
                    </a>
                  </div>
                </div>
              )}
            </div>
            
            {/* Bookmarks Icon - only show for authenticated users */}
            {isAuthenticated && (
              <button
                onClick={() => setIsBookmarksModalOpen(true)}
                className="relative flex items-center justify-center w-8 h-8 rounded-full hover:opacity-80 transition-opacity duration-200"
                title="My Bookmarks"
              >
                <div className="w-8 h-8 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center group hover:bg-black/70 transition-all border border-white/10 shadow-lg">
                  <Bookmark className="w-4 h-4 text-gray-200 group-hover:text-white" />
                </div>
              </button>
            )}
            
            {/* About Icon */}
            <button
              onClick={() => setIsAboutModalOpen(true)}
              className="relative flex items-center justify-center w-8 h-8 rounded-full hover:opacity-80 transition-opacity duration-200"
              title="About"
            >
              <div className="w-8 h-8 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center group hover:bg-black/70 transition-all border border-white/10 shadow-lg">
                <Info className="w-4 h-4 text-gray-200 group-hover:text-white" />
              </div>
            </button>
            
            {/* Report Bug Icon */}
            <button
              onClick={() => setIsReportBugModalOpen(true)}
              className="relative flex items-center justify-center w-8 h-8 rounded-full hover:opacity-80 transition-opacity duration-200"
              title="Report a Bug"
            >
              <div className="w-8 h-8 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center group hover:bg-black/70 transition-all border border-white/10 shadow-lg">
                <Bug className="w-4 h-4 text-gray-200 group-hover:text-white" />
              </div>
            </button>
            
            {/* GPX to Image Tool Icon */}
            <a
              href="/gpx-to-image-app.html"
              className="relative flex items-center justify-center w-8 h-8 rounded-full hover:opacity-80 transition-opacity duration-200"
              title="GPX to Image Tool"
            >
              <div className="w-8 h-8 bg-black/60 backdrop-blur-md rounded-full flex items-center justify-center group hover:bg-black/70 transition-all border border-white/10 shadow-lg">
                <Image className="w-4 h-4 text-gray-200 group-hover:text-white" />
              </div>
            </a>
            
        {/* User Menu */}
        <UserMenu onLoadRoutes={onLoadRoutes} />
      </div>

      {/* Mobile menu button - positioned to align with map controls */}
      <div className="md:hidden fixed z-50" style={{ top: '10px', right: '10px' }}>
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="text-gray-200 hover:bg-gray-700/50 transition-colors flex items-center justify-center bg-gray-800/60 backdrop-blur-sm rounded-lg shadow-lg"
              style={{ width: '32px', height: '32px' }}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {isMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
      </div>

      {/* Mobile menu */}
      <div 
        className={`md:hidden bg-gray-800/95 backdrop-blur-md fixed top-0 left-0 right-0 w-full overflow-hidden transition-all duration-500 ease-in-out z-40 ${
          isMenuOpen ? 'max-h-screen' : 'max-h-0'
        }`}
      >
        <div className="px-2 pt-16 pb-3 space-y-1">
            {/* Communities Section */}
            <div className="px-3 py-2">
              <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Communities</h3>
              <div className="space-y-1">
                <a
                  href="/calendar"
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:text-emerald-400 hover:bg-gray-700 rounded-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Calendar className="w-5 h-5 mr-3" />
                  Events Calendar
                </a>
                <a
                  href="/create-club"
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:text-emerald-400 hover:bg-gray-700 rounded-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  Create a Club
                </a>
                <a
                  href="/clubs"
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:text-emerald-400 hover:bg-gray-700 rounded-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Browse Clubs
                </a>
                <button
                  onClick={() => {
                    setIsAboutModalOpen(true)
                    setIsMenuOpen(false)
                  }}
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:text-emerald-400 hover:bg-gray-700 rounded-md w-full text-left"
                >
                  <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  About
                </button>
                <button
                  onClick={() => {
                    setIsReportBugModalOpen(true)
                    setIsMenuOpen(false)
                  }}
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:text-emerald-400 hover:bg-gray-700 rounded-md w-full text-left"
                >
                  <Bug className="w-5 h-5 mr-3" />
                  Report a Bug
                </button>
                <a
                  href="/gpx-to-image-app.html"
                  className="flex items-center px-3 py-2 text-sm text-gray-300 hover:text-emerald-400 hover:bg-gray-700 rounded-md"
                  onClick={() => setIsMenuOpen(false)}
                >
                  <Image className="w-5 h-5 mr-3" />
                  GPX to Image Tool
                </a>
              </div>
            </div>
            
            <div className="pt-4 border-t border-gray-700">
              <div className="px-3">
                <UserMenu onLoadRoutes={onLoadRoutes} isMobile={true} />
              </div>
            </div>
          </div>
      </div>
      
      {/* Bookmarks Modal */}
      {isAuthenticated && (
        <BookmarksModal
          isOpen={isBookmarksModalOpen}
          onClose={() => setIsBookmarksModalOpen(false)}
        />
      )}
      
      {/* About Modal */}
      <AboutModal
        isOpen={isAboutModalOpen}
        onClose={() => setIsAboutModalOpen(false)}
      />
      
      {/* Report Bug Modal */}
      <ReportBugModal
        isOpen={isReportBugModalOpen}
        onClose={() => setIsReportBugModalOpen(false)}
      />
    </>
  )
}